import { useState, useEffect, useCallback } from 'react';

/**
 * Breakpoint definitions matching Tailwind CSS defaults
 * Mobile: < 768px
 * Tablet: 768px - 1024px
 * Desktop: > 1024px
 */
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const;

export interface ViewportState {
  /** Screen width in pixels */
  width: number;
  /** Screen height in pixels */
  height: number;
  /** True when viewport width is less than 768px */
  isMobile: boolean;
  /** True when viewport width is between 768px and 1024px */
  isTablet: boolean;
  /** True when viewport width is greater than 1024px */
  isDesktop: boolean;
  /** Current device orientation */
  orientation: 'portrait' | 'landscape';
  /** True if user prefers reduced motion */
  prefersReducedMotion: boolean;
  /** True if device supports touch */
  hasTouchSupport: boolean;
}

/**
 * Hook for responsive breakpoint detection and viewport tracking.
 * Provides flags for mobile, tablet, and desktop viewports along with
 * width, height, orientation, and accessibility preferences.
 * 
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop, prefersReducedMotion } = useViewport();
 * 
 * return (
 *   <div className={isMobile ? 'flex-col' : 'flex-row'}>
 *     {!prefersReducedMotion && <AnimatedComponent />}
 *   </div>
 * );
 * ```
 */
export function useViewport(): ViewportState {
  const getViewportState = useCallback((): ViewportState => {
    // Handle SSR - default to mobile-first
    if (typeof window === 'undefined') {
      return {
        width: 0,
        height: 0,
        isMobile: true,
        isTablet: false,
        isDesktop: false,
        orientation: 'portrait',
        prefersReducedMotion: false,
        hasTouchSupport: false,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const isMobile = width < BREAKPOINTS.mobile;
    const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet;
    const isDesktop = width >= BREAKPOINTS.tablet;
    const orientation = width > height ? 'landscape' : 'portrait';
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches ?? false;
    
    // Check for touch support
    const hasTouchSupport = 
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0;

    return {
      width,
      height,
      isMobile,
      isTablet,
      isDesktop,
      orientation,
      prefersReducedMotion,
      hasTouchSupport,
    };
  }, []);

  const [state, setState] = useState<ViewportState>(getViewportState);

  useEffect(() => {
    // Update state on mount (handles SSR hydration)
    setState(getViewportState());

    // Debounce resize handler for performance
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setState(getViewportState());
      }, 100);
    };

    // Listen for reduced motion preference changes
    const reducedMotionQuery = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    );
    const handleReducedMotionChange = () => {
      setState(getViewportState());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
    };
  }, [getViewportState]);

  return state;
}

export default useViewport;
